import { Vector2 } from "../../../common/vector2.js";
import { formatShortDuration } from "../../../common/common_funcs.js";
import { Game } from "../../../game.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";

export interface HappyFallWaitOverlayParams
{
  refill_at: number;
}

export class HappyFallWaitOverlay extends AbstractOverlay
{
  private static readonly DESIGN_W = 1080;
  private static readonly DESIGN_H = 1920;
  private static readonly LOGO_W = 640;
  private static readonly LOGO_H = 297;
  private static readonly LOGO_Y = 415;
  private static readonly TITLE_W = 900;
  private static readonly TITLE_H = 90;
  private static readonly TITLE_Y = 867;
  private static readonly TITLE_FONT = 66;
  private static readonly TIMER_W = 274;
  private static readonly TIMER_H = 72;
  private static readonly TIMER_Y = 1013;
  private static readonly TIMER_BORDER = 5;
  private static readonly CLOCK_SIZE = 56;
  private static readonly CLOCK_X = 8;
  private static readonly CLOCK_RING = 6;
  private static readonly HAND_W = 7;
  private static readonly HAND_LONG = 19;
  private static readonly HAND_SHORT = 15;
  private static readonly TIMER_TEXT_RIGHT = 18;
  private static readonly TIMER_FONT = 40;
  private static readonly BUTTON_W = 554;
  private static readonly BUTTON_H = 240;
  private static readonly BUTTON_Y = 1460;
  private static readonly BUTTON_TEXT_INSET = 60;
  private static readonly BUTTON_FONT = 80;
  private static readonly POP_UP_MS = 176;
  private static readonly POP_SET_MS = 112;
  private static readonly POP_SCALE = 1.15;

  private readonly refill_at: number;
  private readonly body: UIElementHolder;
  private timer_text!: UITextElement;
  private timer_label = "";
  private closed = false;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, params: HappyFallWaitOverlayParams, overlay_id: OverlayId )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );
    this.refill_at = params.refill_at;

    const body_w = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.DESIGN_W );
    const body_h = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.DESIGN_H );
    const body_x = ( width - body_w ) / 2;
    const body_y = ( height - body_h ) / 2;

    this.createBackground();
    this.body = new UIElementHolder( ctx, body_x, body_y, body_w, body_h );
    this.addChild( this.body );

    this.createLogo();
    this.createTitle();
    this.createTimer();
    this.createHomeButton();
    this.setTimerLabel( this.refill_at - Date.now() );
  }

  private createBackground(): void
  {
    const x = -Game.safeAreaLeft();
    const y = -Game.safeAreaTop();
    const w = this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight();
    const h = this.rect.getH() + Game.safeAreaTop() + Game.safeAreaBottom();

    const bg = new RoundedRectangle( this.ctx, x, y, w, h, 0, ColorScheme.happy_fall_intro_bg );
    bg.setClickable( false );
    this.addChild( bg );
  }

  private createLogo(): void
  {
    const w = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.LOGO_W );
    const h = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.LOGO_H );
    const x = ( this.body.getRect().getW() - w ) / 2;
    const y = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.LOGO_Y );

    const logo = new UIImage( this.ctx, x, y, w, h, ImgID.UI_HappyFallLogo );
    logo.setClickable( false );
    this.body.addChild( logo );
  }

  private createTitle(): void
  {
    const w = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.TITLE_W );
    const h = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.TITLE_H );
    const x = ( this.body.getRect().getW() - w ) / 2;
    const y = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.TITLE_Y );

    const title = new UITextElement( this.ctx, x, y, w, h, {
      localization_key: "HAPPY_FALL.NEW_PRIZES_IN",
      font_size: LayoutScaler.textSizeFromDesign( HappyFallWaitOverlay.TITLE_FONT ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_color: ColorScheme.happy_fall_wait_title_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      outline_offset_y: LayoutScaler.yFromDesign( 3 ),
      shadow_offset_x: 0,
      shadow_offset_y: 0,
      auto_wrap: false,
      fit_font_size: true,
    } );
    title.setClickable( false );
    this.body.addChild( title );
  }

  private createTimer(): void
  {
    const w = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.TIMER_W );
    const h = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.TIMER_H );
    const x = ( this.body.getRect().getW() - w ) / 2;
    const y = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.TIMER_Y );
    const border = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.TIMER_BORDER );

    const pill = new RoundedRectangle( this.ctx, x, y, w, h, h / 2, ColorScheme.happy_fall_timer_fill, undefined, false, ColorScheme.happy_fall_timer_border, border );
    pill.setClickable( false );
    this.body.addChild( pill );

    const clock_size = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.CLOCK_SIZE );
    const clock_x = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.CLOCK_X );
    const clock_y = ( h - clock_size ) / 2;
    const ring = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.CLOCK_RING );

    const clock = new RoundedRectangle( this.ctx, clock_x, clock_y, clock_size, clock_size, clock_size / 2, ColorScheme.happy_fall_timer_clock, undefined, false, ColorScheme.white, ring );
    clock.setClickable( false );
    pill.addChild( clock, true );

    const hand_w = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.HAND_W );
    const hand_long = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.HAND_LONG );
    const hand_short = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.HAND_SHORT );
    const hand_x = ( clock_size - hand_w ) / 2;
    const long_y = clock_size / 2 + hand_w / 2 - hand_long;
    const short_y = ( clock_size - hand_w ) / 2;

    const long_hand = new RoundedRectangle( this.ctx, hand_x, long_y, hand_w, hand_long, hand_w / 2, ColorScheme.white );
    long_hand.setClickable( false );
    clock.addChild( long_hand, true );

    const short_hand = new RoundedRectangle( this.ctx, hand_x, short_y, hand_short, hand_w, hand_w / 2, ColorScheme.white );
    short_hand.setClickable( false );
    clock.addChild( short_hand, true );

    const text_x = clock_x + clock_size + LayoutScaler.xFromDesign( 10 );
    const text_w = w - text_x - LayoutScaler.widthFromDesign( HappyFallWaitOverlay.TIMER_TEXT_RIGHT );

    this.timer_text = new UITextElement( this.ctx, text_x, 0, text_w, h, {
      text: "",
      font_size: LayoutScaler.textSizeFromDesign( HappyFallWaitOverlay.TIMER_FONT ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_color: ColorScheme.happy_fall_timer_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      shadow_offset_x: 0,
      shadow_offset_y: 0,
      auto_wrap: false,
      scale_to_width: true,
    } );
    this.timer_text.setClickable( false );
    pill.addChild( this.timer_text, true );
  }

  private createHomeButton(): void
  {
    const w = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.BUTTON_W );
    const h = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.BUTTON_H );
    const x = ( this.body.getRect().getW() - w ) / 2;
    const y = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.BUTTON_Y );

    const button = new UINineSliceElement( this.ctx, x, y, w, h, ImgID.UI_BlueBtn, "blue_btn", undefined, undefined, true );
    button.setOnClick( () => this.close( false ) );
    this.body.addChild( button );

    const text_inset = LayoutScaler.widthFromDesign( HappyFallWaitOverlay.BUTTON_TEXT_INSET );
    const text_w = w - text_inset * 2;

    const text = new UITextElement( this.ctx, text_inset, 0, text_w, h, {
      localization_key: "HAPPY_FALL.HOME",
      font_size: LayoutScaler.textSizeFromDesign( HappyFallWaitOverlay.BUTTON_FONT ),
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.rubik,
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      outline_color: ColorScheme.conveyor_result_return_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_color: ColorScheme.black_transparent,
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      scale_to_width: true,
      auto_wrap: false,
    } );
    button.addChild( text, true );
    this.popIn( button );
  }

  private popIn( element: UIElement ): void
  {
    const start = performance.now();
    const zero = new Vector2( 0, 0 );
    const peak = new Vector2( HappyFallWaitOverlay.POP_SCALE, HappyFallWaitOverlay.POP_SCALE );
    const rest = new Vector2( 1, 1 );

    element.setScale( zero );
    element.addTransform( new TransformScale( element, true, zero, peak, start, HappyFallWaitOverlay.POP_UP_MS, { easing: Easing.EaseOutQuad } ) );
    element.addTransform( new TransformScale( element, true, peak, rest, start + HappyFallWaitOverlay.POP_UP_MS, HappyFallWaitOverlay.POP_SET_MS, { easing: Easing.EaseOutQuad } ) );
  }

  private updateTimer( now_ms: number ): void
  {
    const left_ms = this.refill_at - now_ms;
    if ( left_ms <= 0 )
    {
      this.close( true );
      return;
    }

    this.setTimerLabel( left_ms );
  }

  private setTimerLabel( left_ms: number ): void
  {
    const label = formatShortDuration( left_ms );
    if ( label === this.timer_label )
      return;

    this.timer_label = label;
    this.timer_text.setText( label );
  }

  private close( open_game: boolean ): void
  {
    if ( this.closed )
      return;

    this.closed = true;
    OverlayManager.closeOverlay();
    if ( open_game )
      void ScreenManager.showScreen( Screens.HAPPY_FALL );
  }

  public override update( now: number ): void
  {
    if ( !this.closed )
      this.updateTimer( Date.now() );
    super.update( now );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "HappyFallWaitOverlay";
  }
}
